import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, RefreshCw, UserPlus, X } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { ADMIN_RBAC_CATALOG, getScreenCatalogForRole, normalizeAccess } from "../../../rbac/catalog";
import { filterCatalogByCompanyModules } from "../../../utils/companyModules";
import { DsAlert, DsButton, DsIcon, DsPageHeader } from "../../../design-system";
import RoleListView, { visibleRolesFor } from "./RoleListView";
import RolePermissionMatrix from "./RolePermissionMatrix";
import RoleSummaryPanel from "./RoleSummaryPanel";
import RoleUsersView from "./RoleUsersView";
import RoleWizard from "./RoleWizard";
import ConfirmDialog from "./ConfirmDialog";
import InviteUserModal from "./InviteUserModal";
import {
  ACTION_SHORT_LABELS,
  apiErrorMessage,
  isDefaultSystemRole,
  isModuleGranted,
  isSystemRole,
  moduleStats,
  normalizeData,
  roleUsesAccess,
  sanitizeScreenAccessForSubmit,
  setModuleEnabled,
  toggleScreenAction,
} from "./roleAccessUtils";

const PAGE_INFO =
  "Users: invite people, multi-select for bulk role assign and Require 2FA, or remove (suspend + Employee / Self). Roles: create with role type + modules (+ who gets it); refine screen actions and data scope on Permissions. Card menu: Info, Copy role permissions, Deactivate, Delete. Import / Export has Roles Excel to download grants and import new or updated custom roles.";

const TABS = [
  { id: "users", label: "Users" },
  { id: "roles", label: "Roles" },
  { id: "matrix", label: "Permissions" },
];

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj || {}));
}

function diffAccess(saved, draft, catalog) {
  const changes = [];
  for (const mod of catalog || []) {
    for (const screen of mod.screens || []) {
      for (const action of screen.actions || []) {
        const was = !!saved?.[mod.key]?.screens?.[screen.key]?.[action];
        const is = !!draft?.[mod.key]?.screens?.[screen.key]?.[action];
        if (was !== is) {
          changes.push({
            module: mod.label,
            screen: screen.label,
            action: ACTION_SHORT_LABELS[action] || action,
            granted: is,
          });
        }
      }
    }
  }
  return changes;
}

/**
 * Users & roles page: Users | Roles | Permissions. Edits go through wizard or draft matrix save.
 */
export default function UserRoleManagement({
  roles,
  fetchRoles,
  rolesMeta,
  setFormData,
  filterResults,
  setFilterResults,
  setShowFilters,
  branches,
  departments,
}) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();

  const canViewUserRoles =
    isAdmin ||
    hasModuleAccess("user_roles", "view") ||
    hasScreenAccess("user_roles", "role_list", "view") ||
    hasScreenAccess("user_roles", "screen_access", "view") ||
    hasScreenAccess("user_roles", "data_access", "view");
  const canCreateRole =
    isAdmin || hasModuleAccess("user_roles", "create") || hasScreenAccess("user_roles", "role_list", "create");
  const canEditRole =
    isAdmin || hasModuleAccess("user_roles", "edit") || hasScreenAccess("user_roles", "role_list", "edit");
  const canDeleteRole =
    isAdmin || hasModuleAccess("user_roles", "delete") || hasScreenAccess("user_roles", "role_list", "delete");
  const canEditScreenAccess =
    isAdmin || hasModuleAccess("user_roles", "edit") || hasScreenAccess("user_roles", "screen_access", "edit");
  const canEditDataAccess =
    isAdmin || hasModuleAccess("user_roles", "edit") || hasScreenAccess("user_roles", "data_access", "edit");
  const canManageUsers = isAdmin;
  const canAssignRole = isAdmin;

  const [mainTab, setMainTab] = useState("users");
  const [search, setSearch] = useState("");
  const [showDefaultRoles, setShowDefaultRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [rawScreenAccess, setRawScreenAccess] = useState({});
  const [dataByRole, setDataByRole] = useState({});
  const [draftAccess, setDraftAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [infoRoleId, setInfoRoleId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const loadAccess = useCallback(async () => {
    setAccessLoading(true);
    setAccessError(null);
    try {
      const [screenRes, dataRes] = await Promise.all([
        api.get("/global-settings/screen-access"),
        api.get("/global-settings/data-access"),
      ]);
      setRawScreenAccess(screenRes?.data?.data || {});
      setDataByRole(dataRes?.data?.data || {});
      setDraftAccess(null);
    } catch (err) {
      setAccessError(apiErrorMessage(err, "Could not load role permissions."));
    } finally {
      setAccessLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewUserRoles) loadAccess();
  }, [canViewUserRoles, loadAccess]);

  const loadRoleUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await api.get("/global-settings/role-users");
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRoleUsers(list);
      setUsersLoaded(true);
      setSelectedUserId((prev) => {
        if (prev && list.some((u) => String(u.userId) === String(prev))) return prev;
        return list[0]?.userId ?? null;
      });
    } catch (err) {
      setUsersError(apiErrorMessage(err, "Could not load users."));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewUserRoles && (mainTab === "users" || !usersLoaded) && !usersLoading && !usersLoaded) {
      loadRoleUsers();
    }
  }, [canViewUserRoles, mainTab, usersLoaded, usersLoading, loadRoleUsers]);

  const roleList = useMemo(() => (Array.isArray(roles) ? roles : []), [roles]);

  const accessByRole = useMemo(() => {
    const out = {};
    for (const r of roleList) {
      out[r.id] = normalizeAccess(rawScreenAccess[String(r.id)] || {}, getScreenCatalogForRole(r));
    }
    return out;
  }, [roleList, rawScreenAccess]);

  const delegate = useMemo(() => {
    if (isAdmin) return null;
    const hinted = roleList.find((r) => Array.isArray(r.editableModules));
    const own = roleList.find((r) => r.isOwnRole);
    const ownData = own ? normalizeData(dataByRole[String(own.id)]) : null;
    return {
      modules: new Set(hinted?.editableModules || []),
      ownScope: ownData?.level ? ownData : { level: "own", selectedItems: [] },
    };
  }, [isAdmin, roleList, dataByRole]);

  const visibleRoles = useMemo(
    () => visibleRolesFor(roleList, { search, showDefaultRoles }),
    [roleList, search, showDefaultRoles]
  );

  useEffect(() => {
    if (!visibleRoles.some((r) => String(r.id) === String(selectedRoleId))) {
      setSelectedRoleId(visibleRoles[0]?.id ?? null);
    }
  }, [visibleRoles, selectedRoleId]);

  const selectedRole = roleList.find((r) => String(r.id) === String(selectedRoleId)) || null;
  const selectedUsesAccess = roleUsesAccess(selectedRole);
  const selectedCatalog = useMemo(
    () => filterCatalogByCompanyModules(getScreenCatalogForRole(selectedRole)),
    [selectedRole]
  );
  const savedAccess = selectedRole ? accessByRole[selectedRole.id] : null;
  const selectedAccess =
    draftAccess && selectedRole && String(draftAccess.roleId) === String(selectedRole.id)
      ? draftAccess.access
      : savedAccess;

  const pendingChanges = useMemo(() => {
    if (!selectedRole || !draftAccess || String(draftAccess.roleId) !== String(selectedRole.id)) return [];
    return diffAccess(savedAccess, draftAccess.access, selectedCatalog);
  }, [selectedRole, draftAccess, savedAccess, selectedCatalog]);

  const infoRole = roleList.find((r) => String(r.id) === String(infoRoleId)) || null;
  const infoCatalog = useMemo(
    () => filterCatalogByCompanyModules(getScreenCatalogForRole(infoRole)),
    [infoRole]
  );
  const infoAccess = infoRole ? accessByRole[infoRole.id] : null;
  const infoStats = useMemo(() => {
    const onModules = infoCatalog.filter((m) => isModuleGranted(m, infoAccess?.[m.key]));
    return {
      modulesOn: onModules.length,
      modulesTotal: infoCatalog.length,
      permissions: onModules.reduce((sum, m) => sum + moduleStats(m, infoAccess?.[m.key]).granted, 0),
    };
  }, [infoCatalog, infoAccess]);

  const getBlockers = useCallback(
    (role) => {
      if (!role) return {};
      const system = isSystemRole(role);
      const catalog = filterCatalogByCompanyModules(getScreenCatalogForRole(role));
      const access = accessByRole[role.id];
      const own = role.isOwnRole ? "You can't change your own role." : null;
      const outside =
        delegate &&
        catalog.some((m) => !delegate.modules.has(m.key) && isModuleGranted(m, access?.[m.key]));
      const outsideMsg = (what) =>
        `This role includes modules you can't grant, so only an admin can ${what} it.`;
      const canEditThis = system ? canEditScreenAccess || canEditDataAccess : canEditRole;
      return {
        edit: !canEditThis ? "You don't have permission to edit roles." : own,
        copy: !canCreateRole ? "You don't have permission to create roles." : null,
        toggle: !canEditRole
          ? "You don't have permission to edit roles."
          : own || (outside && role.active ? outsideMsg("deactivate") : null),
        delete: !canDeleteRole
          ? "You don't have permission to delete roles."
          : own || (outside ? outsideMsg("delete") : null),
      };
    },
    [
      accessByRole,
      delegate,
      canEditRole,
      canCreateRole,
      canDeleteRole,
      canEditScreenAccess,
      canEditDataAccess,
    ]
  );

  const afterChange = async () => {
    await Promise.all([
      fetchRoles(),
      loadAccess(),
      usersLoaded || mainTab === "users" ? loadRoleUsers() : Promise.resolve(),
    ]);
  };

  const ensureDraft = () => {
    if (!selectedRole) return null;
    if (draftAccess && String(draftAccess.roleId) === String(selectedRole.id)) return draftAccess.access;
    const cloned = deepClone(savedAccess || {});
    setDraftAccess({ roleId: selectedRole.id, access: cloned });
    return cloned;
  };

  const handleToggleAction = (mod, screenKey, action, value) => {
    if (!selectedRole || isSystemRole(selectedRole) || !canEditScreenAccess) return;
    const base = ensureDraft() || deepClone(savedAccess || {});
    const nextMod = toggleScreenAction(base[mod.key], mod, screenKey, action, value);
    const next = { ...base, [mod.key]: nextMod };
    setDraftAccess({ roleId: selectedRole.id, access: next });
  };

  const discardDraft = () => {
    setDraftAccess(null);
    setReviewOpen(false);
    toast.success("Changes discarded");
  };

  const saveDraft = async () => {
    if (!selectedRole || !draftAccess) return;
    setBusy(true);
    try {
      const screenAccess = sanitizeScreenAccessForSubmit(draftAccess.access);
      await api.post("/global-settings/screen-access", { roleId: selectedRole.id, screenAccess });
      toast.success("Permissions saved");
      setDraftAccess(null);
      setReviewOpen(false);
      await loadAccess();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not save permissions."));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (role, active) => {
    setBusy(true);
    try {
      await api.put(`/global-settings/roles/${role.id}`, { active });
      toast.success(active ? `${role.name} reactivated.` : `${role.name} deactivated.`);
      setConfirm(null);
      await afterChange();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update the role status."));
    } finally {
      setBusy(false);
    }
  };

  const setSidebarIcons = async (role, enabled) => {
    setBusy(true);
    try {
      const access = accessByRole[role.id] || {};
      const screenAccess = sanitizeScreenAccessForSubmit({
        ...access,
        ess_quick_panel: setModuleEnabled(access.ess_quick_panel, enabled),
      });
      await api.post("/global-settings/screen-access", { roleId: role.id, screenAccess });
      toast.success(`Request & Approvals ${enabled ? "shown" : "hidden"} for ${role.name}.`);
      setConfirm(null);
      await loadAccess();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update the sidebar icons."));
    } finally {
      setBusy(false);
    }
  };

  const deleteRole = async (role) => {
    setBusy(true);
    try {
      await api.delete(`/global-settings/roles/${role.id}`);
      toast.success(`${role.name} deleted.`);
      setConfirm(null);
      setSelectedRoleId(null);
      setInfoRoleId((id) => (String(id) === String(role.id) ? null : id));
      await afterChange();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not delete the role."));
    } finally {
      setBusy(false);
    }
  };

  const handleRoleAction = useCallback(
    (kind, role) => {
      if (!role) return;
      setSelectedRoleId(role.id);
      if (kind === "openPermissions") {
        setMainTab("matrix");
        return;
      }
      if (kind === "info") {
        setInfoRoleId(role.id);
        return;
      }
      if (kind === "edit") {
        setWizard({ role });
        return;
      }
      if (kind === "copy") {
        setWizard({ copyFromRoleId: role.id });
        return;
      }
      if (kind === "toggle") {
        if (role.active) setConfirm({ kind: "deactivate", role });
        else setActive(role, true);
        return;
      }
      if (kind === "delete") setConfirm({ kind: "delete", role });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const changeUserRole = async (user, roleCode) => {
    if (!user?.userId) return;
    setBusyUserId(user.userId);
    try {
      await api.put(`/global-settings/users/${user.userId}/roles`, { roleCodes: [roleCode] });
      toast.success(`${user.name} is now ${roleCode}`);
      await loadRoleUsers();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update the user role."));
    } finally {
      setBusyUserId(null);
    }
  };

  const toggleUserActive = async (user) => {
    if (!user?.userId) return;
    const next =
      user.status === "Suspended" || user.status === "Inactive" ? "Active" : "Suspended";
    setBusyUserId(user.userId);
    try {
      await api.put(`/global-settings/users/${user.userId}/status`, { status: next });
      toast.success(`${user.name} ${next === "Active" ? "activated" : "suspended"}`);
      await loadRoleUsers();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update account status."));
    } finally {
      setBusyUserId(null);
    }
  };

  const toggleUser2fa = async (user) => {
    if (!user?.userId) return;
    setBusyUserId(user.userId);
    try {
      await api.put(`/global-settings/users/${user.userId}/require-2fa`, {
        require2fa: !user.require2fa,
      });
      toast.success(`2FA ${user.require2fa ? "off" : "on"} for ${user.name}`);
      await loadRoleUsers();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update 2FA."));
    } finally {
      setBusyUserId(null);
    }
  };

  const removeUser = async (user) => {
    if (!user?.userId) return;
    setBusyUserId(user.userId);
    try {
      await api.post(`/global-settings/users/${user.userId}/remove`);
      toast.success(`${user.name} removed (suspended + Employee / Self)`);
      setConfirm(null);
      await loadRoleUsers();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not remove user."));
    } finally {
      setBusyUserId(null);
    }
  };

  const bulkAssignRole = async (users, roleCode) => {
    if (!users?.length || !roleCode) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const user of users) {
        try {
          await api.put(`/global-settings/users/${user.userId}/roles`, { roleCodes: [roleCode] });
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      if (ok) toast.success(`Assigned ${roleCode} to ${ok} user${ok === 1 ? "" : "s"}.`);
      if (fail) toast.error(`${fail} user${fail === 1 ? "" : "s"} could not be updated.`);
      await loadRoleUsers();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkRequire2fa = async (users, require2fa) => {
    if (!users?.length) return;
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const user of users) {
        try {
          await api.put(`/global-settings/users/${user.userId}/require-2fa`, { require2fa: !!require2fa });
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      if (ok) {
        toast.success(
          require2fa
            ? `Require 2FA on for ${ok} user${ok === 1 ? "" : "s"}.`
            : `Require 2FA cleared for ${ok} user${ok === 1 ? "" : "s"}.`
        );
      }
      if (fail) toast.error(`${fail} user${fail === 1 ? "" : "s"} could not be updated.`);
      await loadRoleUsers();
    } finally {
      setBulkBusy(false);
    }
  };

  const inviteUser = async ({ name, email, roleCode }) => {
    setBusy(true);
    try {
      await api.post("/global-settings/users/invite", { name, email, roleCode });
      toast.success(`Invite sent to ${email}`);
      setInviteOpen(false);
      await loadRoleUsers();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not send invite."));
    } finally {
      setBusy(false);
    }
  };

  if (!canViewUserRoles) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <DsAlert tone="danger">You do not have access to User Role Management.</DsAlert>
      </div>
    );
  }

  const delegateModuleNames = delegate
    ? filterCatalogByCompanyModules(ADMIN_RBAC_CATALOG)
        .filter((m) => delegate.modules.has(m.key))
        .map((m) => m.label)
    : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-[hsl(var(--ds-bg))] p-4">
      <DsPageHeader
        className="!mb-0 shrink-0"
        title="Users & roles"
        infoTooltip={PAGE_INFO}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-[11px] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-0.5">
              {TABS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={mainTab === opt.id}
                  onClick={() => setMainTab(opt.id)}
                  className={`h-8 rounded-[7px] px-3.5 text-sm font-medium transition ${
                    mainTab === opt.id
                      ? "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {mainTab === "users" ? (
              <DsButton onClick={() => setInviteOpen(true)} disabled={!canManageUsers}>
                <DsIcon icon={UserPlus} size="sm" />
                Invite user
              </DsButton>
            ) : null}
            {mainTab === "roles" ? (
              <DsButton onClick={() => setWizard({})} disabled={!canCreateRole}>
                <DsIcon icon={Plus} size="sm" />
                New role
              </DsButton>
            ) : null}
          </div>
        }
      />

      {(mainTab === "roles" || mainTab === "matrix") && accessError ? (
        <DsAlert tone="danger" className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <span>{accessError}</span>
          <DsButton size="sm" variant="outline" onClick={loadAccess}>
            <DsIcon icon={RefreshCw} size="sm" />
            Retry
          </DsButton>
        </DsAlert>
      ) : null}

      {(mainTab === "roles" || mainTab === "matrix") && delegate ? (
        <DsAlert tone="info" className="shrink-0">
          You can manage roles for{" "}
          <strong>{delegateModuleNames.length ? delegateModuleNames.join(", ") : "no modules yet"}</strong>.
          Other modules stay locked. You can&apos;t edit your own role.
        </DsAlert>
      ) : null}

      {mainTab === "users" ? (
        <RoleUsersView
          users={roleUsers}
          loading={usersLoading}
          error={usersError}
          onRetry={loadRoleUsers}
          roles={roleList}
          accessByRole={accessByRole}
          canManageUsers={canManageUsers}
          canAssignRole={canAssignRole}
          busyUserId={busyUserId}
          bulkBusy={bulkBusy}
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
          onChangeRole={changeUserRole}
          onToggleActive={toggleUserActive}
          onToggle2fa={toggleUser2fa}
          onRemoveUser={(user) => setConfirm({ kind: "removeUser", user })}
          onBulkAssignRole={bulkAssignRole}
          onBulkRequire2fa={bulkRequire2fa}
          filterResults={filterResults}
          onOpenFilters={() => {
            setFormData?.((prev) => ({ ...prev, filterData: filterResults || null }));
            setShowFilters?.(true);
          }}
        />
      ) : null}

      {mainTab === "roles" ? (
        <RoleListView
          layout="cards"
          roles={roleList}
          visibleRoles={visibleRoles}
          search={search}
          setSearch={setSearch}
          showDefaultRoles={showDefaultRoles}
          setShowDefaultRoles={setShowDefaultRoles}
          selectedRoleId={selectedRoleId}
          onSelectRole={setSelectedRoleId}
          isDelegate={!!delegate}
          getBlockers={getBlockers}
          onRoleAction={handleRoleAction}
          accessByRole={accessByRole}
          onNewRole={() => setWizard({})}
          canCreateRole={canCreateRole}
        />
      ) : null}

      {mainTab === "matrix" ? (
        <RolePermissionMatrix
          key={selectedRole?.id ?? "none"}
          role={selectedRole}
          roles={visibleRoles.length ? visibleRoles : roleList}
          catalog={selectedCatalog}
          access={selectedAccess}
          savedAccess={savedAccess}
          usesAccess={selectedUsesAccess}
          loading={accessLoading}
          editable={canEditScreenAccess}
          locked={isSystemRole(selectedRole)}
          dataAccess={selectedRole ? normalizeData(dataByRole[String(selectedRole.id)]) : null}
          onChangeRole={(id) => {
            if (pendingChanges.length) {
              toast.error("Review or discard unsaved changes before switching roles.");
              return;
            }
            setSelectedRoleId(id);
            setDraftAccess(null);
          }}
          onToggleAction={handleToggleAction}
          onEditRole={
            canEditRole
              ? (role) => {
                  if (pendingChanges.length) {
                    toast.error("Review or discard unsaved changes before editing the role.");
                    return;
                  }
                  setWizard({ role });
                }
              : null
          }
          pendingCount={pendingChanges.length}
          onDiscard={discardDraft}
          onReviewSave={() => setReviewOpen(true)}
          onSidebarToggle={
            isDefaultSystemRole(selectedRole) && !delegate && canEditScreenAccess
              ? (enabled) => setConfirm({ kind: "sidebarIcons", role: selectedRole, enabled })
              : null
          }
        />
      ) : null}

      {infoRole
        ? createPortal(
            <div
              className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/50 p-4"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setInfoRoleId(null);
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="urm-role-info-title"
                className="flex max-h-[min(85vh,640px)] w-full max-w-md flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
              >
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
                  <h3 id="urm-role-info-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
                    Role info
                  </h3>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setInfoRoleId(null)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                  >
                    <DsIcon icon={X} size="sm" />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  <RoleSummaryPanel
                    role={infoRole}
                    isSystem={isSystemRole(infoRole)}
                    usesAccess={roleUsesAccess(infoRole)}
                    stats={infoStats}
                    dataAccess={normalizeData(dataByRole[String(infoRole.id)])}
                    branches={branches}
                    departments={departments}
                    showActions={false}
                    className="flex min-h-0 flex-col overflow-hidden bg-transparent"
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {reviewOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[10010] grid place-items-center bg-black/50 p-4"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !busy) setReviewOpen(false);
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="urm-review-title"
                className="flex max-h-[85dvh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
              >
                <div className="border-b border-[hsl(var(--ds-border))] px-5 py-4">
                  <h3 id="urm-review-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
                    Review {pendingChanges.length} change{pendingChanges.length > 1 ? "s" : ""}
                  </h3>
                </div>
                <div className="scroll-area min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-3">
                  {pendingChanges.map((c, i) => (
                    <div key={`${c.module}-${c.screen}-${c.action}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                      <span>
                        <strong>{selectedRole?.name}</strong> · {c.module} · {c.screen} · {c.action}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          c.granted
                            ? "bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]"
                            : "bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))]"
                        }`}
                      >
                        {c.granted ? "Granted" : "Removed"}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
                  <DsButton variant="outline" size="sm" onClick={() => setReviewOpen(false)} disabled={busy}>
                    Keep editing
                  </DsButton>
                  <DsButton size="sm" onClick={saveDraft} loading={busy}>
                    Save changes
                  </DsButton>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <InviteUserModal
        open={inviteOpen}
        roles={roleList}
        loading={busy}
        onClose={() => setInviteOpen(false)}
        onSubmit={inviteUser}
      />

      {wizard ? (
        <RoleWizard
          target={wizard}
          roles={roleList}
          accessByRole={accessByRole}
          dataByRole={dataByRole}
          branches={branches}
          departments={departments}
          filterResults={filterResults}
          setFilterResults={setFilterResults}
          setShowFilters={setShowFilters}
          setFormData={setFormData}
          activeEmployeeCount={rolesMeta?.activeEmployeeCount}
          isAdmin={isAdmin}
          delegate={delegate}
          canEditScreenAccess={canEditScreenAccess}
          canEditDataAccess={canEditDataAccess}
          onClose={() => setWizard(null)}
          onRoleCreated={() => fetchRoles()}
          onSaved={async (roleId) => {
            setWizard(null);
            setShowDefaultRoles((prev) => prev || isSystemRole(roleList.find((r) => String(r.id) === String(roleId))));
            await afterChange();
            setSelectedRoleId(roleId);
            setMainTab("matrix");
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirm?.kind === "deactivate"}
        tone="warning"
        title={`Deactivate ${confirm?.role?.name || "role"}?`}
        confirmLabel="Deactivate"
        loading={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => setActive(confirm.role, false)}
      >
        {confirm?.role?.assignedUserCount
          ? `${confirm.role.assignedUserCount} assigned employee(s) will get only default employee access until you reactivate it.`
          : "No employees are assigned to this role right now."}{" "}
        The role&apos;s settings are kept.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "sidebarIcons"}
        tone="warning"
        title={`${confirm?.enabled ? "Show" : "Hide"} Request & Approvals for ${confirm?.role?.name || "role"}?`}
        confirmLabel={confirm?.enabled ? "Show" : "Hide"}
        loading={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => setSidebarIcons(confirm.role, confirm.enabled)}
      >
        Employees with this role{" "}
        {confirm?.enabled
          ? "will see the Request and Approvals icons again."
          : "will no longer see the Request and Approvals icons."}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "delete"}
        title={`Delete ${confirm?.role?.name || "role"}?`}
        confirmLabel="Delete role"
        loading={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => deleteRole(confirm.role)}
      >
        This permanently removes the role and its permissions.
        {confirm?.role?.assignedUserCount
          ? ` ${confirm.role.assignedUserCount} assigned employee(s) go back to default employee access.`
          : ""}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "removeUser"}
        tone="warning"
        title={`Remove ${confirm?.user?.name || "user"}?`}
        confirmLabel="Remove user"
        loading={!!busyUserId}
        onCancel={() => setConfirm(null)}
        onConfirm={() => removeUser(confirm.user)}
      >
        This suspends the account and resets them to Employee / Self. Employee and payroll history are kept.
      </ConfirmDialog>
    </div>
  );
}
